import { useState, useCallback } from 'react';
import { ComboboxOption } from '@cmhc-schl/hearth-ui';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import {
  DropdownOptionType,
  FilterValuesType,
  TranslationType
} from '../../HbtMasterPool/components/HbtMasterPoolTable/types';
import { requestTypeMapping as masterPoolRequestTypeMapping } from '../../HbtMasterPool/utils/RequestTypeMapping';
import { requestTypeMapping as portfolioManagementRequestTypeMapping } from '../../HbtPortfolioManagement/utils/RequestTypeMapping';

export type FilterConfig = {
  initialFilterValues: FilterValuesType;
  translation?: string;
};

export const useInventoryFilters = ({ initialFilterValues, translation }: FilterConfig) => {
  const [filterValues, setFilterValues] = useState<FilterValuesType>(initialFilterValues);
  const [activeFilters, setActiveFilters] = useState<FilterValuesType>(initialFilterValues);
  const { t } = useTranslation(translation);

  const requestTypeMapping =
    translation === 'HbtMasterPool'
      ? masterPoolRequestTypeMapping
      : portfolioManagementRequestTypeMapping;

  const getRequestTypeLabel = useCallback(
    (requestType: keyof typeof requestTypeMapping, t: TranslationType) => {
      return requestTypeMapping(t)[requestType];
    },
    [requestTypeMapping]
  );

  const clearFilter = useCallback(() => {
    const clearedValues = { ...initialFilterValues };
    setFilterValues(clearedValues);
    setActiveFilters(clearedValues);
  }, [initialFilterValues]);

  const changeFilter = useCallback(
    (
      filterType: string,
      selectedValue: DropdownOptionType | ComboboxOption | boolean | Date | string
    ) => {
      if (filterType === 'requestType') {
        return;
      } else if (filterType.includes('Date')) {
        setFilterValues({
          ...filterValues,
          [filterType]: selectedValue
        });
      } else {
        setFilterValues((prevState) => ({
          ...prevState,
          [filterType]: selectedValue
        }));
      }
    },
    []
  );

  const getChips = useCallback(() => {
    const chips: { label: string; key: string }[] = [];

    activeFilters.requestType?.forEach((item: string) =>
      chips.push({
        label: getRequestTypeLabel(item as keyof typeof requestTypeMapping, t),
        key: `requestType-${item}`
      })
    );

    activeFilters.status?.forEach((item: DropdownOptionType) =>
      chips.push({ label: item.label, key: `status-${item.value}` })
    );

    activeFilters.ialn?.forEach((item: DropdownOptionType) =>
      chips.push({ label: item.label, key: `ialn-${item.value}` })
    );

    if (activeFilters.startDate || activeFilters.endDate) {
      const from = activeFilters.startDate
        ? moment(activeFilters.startDate).format('YYYY-MM-DD')
        : '';
      const to = activeFilters.endDate ? moment(activeFilters.endDate).format('YYYY-MM-DD') : '';
      chips.push({
        label: `${from} - ${to}`,
        key: `requestReceiptDate`
      });
    }

    if (translation === 'HbtMasterPool') {
      activeFilters.masterPoolNumber?.forEach((item: DropdownOptionType) =>
        chips.push({ label: item.label, key: `masterPoolNumber-${item.value}` })
      );

      if (activeFilters.premiumDiscountIndicator) {
        chips.push({
          label: `${t('premiumDiscountIndicator')} : ${t(`${activeFilters.premiumDiscountIndicator}`)}`,
          key: `premiumDiscountIndicator-${activeFilters.premiumDiscountIndicator}`
        });
      }

      if (activeFilters.fraudDetectionIndicator) {
        chips.push({
          label: `${t('fraudDetectionIndicator')} : ${t(`${activeFilters.fraudDetectionIndicator}`)}`,
          key: `fraudDetectionIndicator-${activeFilters.fraudDetectionIndicator}`
        });
      }
    }

    return chips;
  }, [activeFilters]);

  const deleteChip = useCallback(
    (chip: { key: string }) => {
      const [type, value] = chip.key.split('-');
      const getFilterValues = (prev: FilterValuesType) => {
        const updated = { ...prev };
        if (type === 'requestType') {
          updated.requestType = prev.requestType?.filter((item: string) => item !== value);
        } else if (type === 'status') {
          updated.status = prev.status?.filter((item: DropdownOptionType) => item.value !== value);
        } else if (type === 'ialn') {
          updated.ialn = prev.ialn?.filter((item: DropdownOptionType) => item.value !== value);
        } else if (type === 'requestReceiptDate') {
          updated.startDate = undefined;
          updated.endDate = undefined;
        } else if (type === 'masterPoolNumber') {
          updated.masterPoolNumber = prev.masterPoolNumber?.filter(
            (item: DropdownOptionType) => item.value !== value
          );
        } else if (type === 'premiumDiscountIndicator') {
          updated.premiumDiscountIndicator = undefined;
        } else if (type === 'fraudDetectionIndicator') {
          updated.fraudDetectionIndicator = undefined;
        }
        return updated;
      };
      const nextFiltersValues = getFilterValues(filterValues);
      setFilterValues(nextFiltersValues);

      const getActiveFilters = (prev: FilterValuesType) => {
        const updated = { ...prev };
        if (type === 'requestType') {
          updated.requestType = prev.requestType?.filter((item: string) => item !== value);
        } else if (type === 'status') {
          updated.status = prev.status?.filter((item: DropdownOptionType) => item.value !== value);
        } else if (type === 'ialn') {
          updated.ialn = prev.ialn?.filter((item: DropdownOptionType) => item.value !== value);
        } else if (type === 'requestReceiptDate') {
          updated.startDate = undefined;
          updated.endDate = undefined;
        } else if (type === 'masterPoolNumber') {
          updated.masterPoolNumber = prev.masterPoolNumber?.filter(
            (item: DropdownOptionType) => item.value !== value
          );
        } else if (type === 'premiumDiscountIndicator') {
          updated.premiumDiscountIndicator = undefined;
        } else if (type === 'fraudDetectionIndicator') {
          updated.fraudDetectionIndicator = undefined;
        }
        return updated;
      };

      const nextActiveFilters = getActiveFilters(activeFilters);
      setActiveFilters(nextActiveFilters);

      return { nextActiveFilters, nextFiltersValues };
    },
    [activeFilters, filterValues]
  );

  return {
    filterValues,
    setFilterValues,
    clearFilter,
    changeFilter,
    deleteChip,
    getChips,
    activeFilters,
    setActiveFilters
  };
};
